import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CCardBody } from '@coreui/react-pro'
import { Table, TableProps, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
  useColumnLocalFilter,
  useColumnLocalSort,
  useColumnSearch,
  useTableSearchParams,
} from 'src/libs'
import {
  colorGiftOrderStatuses,
  EGiftOrderStatuses,
  giftOrderFilterStatuses,
} from '../../../giftshop/giftOrders/const'
import { ErrorScreen } from 'src/shared/components/errorScreen/errorScreen'
import { setCrownsRequestError } from './model'
import { useCrownsList } from './hooks'
import { operationTypes } from './constants'
import { Crown } from './types'

type Props = {
  entityId: string
}
export const CrownList = ({ entityId }: Props) => {
  const [searchParams] = useSearchParams()

  const {
    crowns,
    totalCountOfCrowns,
    loading,
    onChangeCrownsList,
    errorCrownsRequest,
  } = useCrownsList(entityId)

  const getFilters = (): Record<string, string | string[]> => {
    const filters: Record<string, string | string[]> = {}

    const sorterField = searchParams.get('sorterField')
    if (sorterField) {
      filters['order'] = sorterField
    }

    const type = searchParams.get('filterField_type')
    if (type) {
      filters['type'] = type.split(',')
    }

    const detail = searchParams.get('searchField_detail')
    if (detail) {
      filters['detail'] = detail
    }

    const status = searchParams.get('filterField_status')
    if (status) {
      filters['status'] = status.split(',')
    }

    return filters
  }

  const searchFields = ['detail']
  const filterFields = ['status', 'type']
  const sorterFields = ['datetime', 'detail', 'amount']

  const handleTableChange: TableProps<Crown>['onChange'] = useTableSearchParams(
    {
      searchFields,
      filterFields,
      sorterFields,
      usePagination: true,
    },
  )

  useEffect(() => {
    onChangeCrownsList({
      clientId: entityId,
      limit: Number(searchParams.get('limit')) || 10,
      page: Number(searchParams.get('page')) || 1,
      order: searchParams.get('sorterField') ?? '-datetime',
      filters: getFilters(),
    })
  }, [searchParams])

  const csvContent = crowns
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  const detailSearch = useColumnSearch<Crown>({
    dataIndex: 'detail',
    placeholder: 'Detail',
  })

  const columns: ColumnsType<Crown> = [
    {
      title: 'Datetime',
      dataIndex: 'datetime',
      sorter: true,
      ...useColumnLocalSort<Crown>({
        dataIndex: 'datetime',
        initialValue: 'descend',
      }),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      filters: operationTypes,
      render: (item: string) => <Tag>{item}</Tag>,
      onFilter: (value: any, record) => record.type == value,
      ...useColumnLocalFilter<Crown>({ dataIndex: 'type' }),
    },
    {
      title: 'Detail',
      dataIndex: 'detail',
      sorter: true,
      ...useColumnLocalSort<Crown>({
        dataIndex: 'detail',
      }),
      ...detailSearch,
    },
    {
      title: 'Crown',
      dataIndex: 'amount',
      sorter: true,
      ...useColumnLocalSort<Crown>({
        dataIndex: 'amount',
      }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (item: string) => (
        <Tag color={colorGiftOrderStatuses(item)}>
          {EGiftOrderStatuses[item as keyof typeof EGiftOrderStatuses]}
        </Tag>
      ),
      filters: giftOrderFilterStatuses,
      onFilter: (value: any, record) => record.status == value,
      ...useColumnLocalFilter<Crown>({ dataIndex: 'status' }),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item: Crown) => {
        return <></>
      },
    },
  ]

  if (![0, 404].includes(errorCrownsRequest.code)) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          onChangeCrownsList({
            clientId: entityId,
            limit: Number(searchParams.get('limit')) || 10,
            page: Number(searchParams.get('page')) || 1,
            order: searchParams.get('sorterField') ?? '-datetime',
            filters: getFilters(),
          })
          setCrownsRequestError({
            message: '',
            code: 0,
          })
        }}
      />
    )
  }
  const current = Number(searchParams.get('page')) || 1
  const pageSize = Number(searchParams.get('limit')) || 10
  return (
    <CCardBody>
      <Table
        dataSource={crowns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        columns={columns}
        rowKey="operationId"
        pagination={{
          current,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfCrowns,
        }}
        onChange={handleTableChange}
      />
    </CCardBody>
  )
}
